import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useRef, useState } from 'react';
import type { CheckSummaryDto } from '@ops-monitor/shared';
import type { CheckRow as RowShape } from '../api/types';

import { monitoring } from '../api/client';
import { AlertSettings } from './AlertSettings';
import { Button } from './ui';
import { HourlyChart } from './HourlyChart';
import { Sparkline } from './Sparkline';
import { StatusDot } from './StatusDot';

/**
 * Turns a check value into renderable rows. Checks return either arrays or
 * `{statuses: [...], scalar: n}`-shaped summaries — in the latter case the
 * array field IS the table (the scalars are already in the tile text).
 * This is also the crash guard: React cannot render raw objects (#31).
 */
export function buildRows(value: unknown): RowShape[] {
  if (Array.isArray(value)) return value as RowShape[];
  if (value !== null && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const arrayFields = Object.values(record).filter((v): v is unknown[] => Array.isArray(v));
    if (arrayFields.length > 0) {
      // longest array wins as the table body
      const longest = arrayFields.reduce((a, b) => (b.length > a.length ? b : a), []);
      return longest as RowShape[];
    }
    return [record as RowShape];
  }
  return [];
}

/** Any non-primitive cell degrades to JSON text — never a React crash. */
function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'да' : 'нет';
  if (typeof value === 'number') return value.toLocaleString('ru-RU');
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      return new Date(value).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    }
    return value;
  }
  return JSON.stringify(value).slice(0, 240);
}

/**
 * Drill-down drawer: why the check exists, the current value (table view for
 * tabular checks — every chart's numbers are always available as a table),
 * the 24 h sparkline, a manual "run now" and per-check Slack alert settings.
 */
export function CheckDrawer({ check, onClose }: { check: CheckSummaryDto; onClose: () => void }) {
  const queryClient = useQueryClient();
  const scrimRef = useRef<HTMLDivElement>(null);
  const [ranAt, setRanAt] = useState(check.ranAt);

  const history = useQuery({
    queryKey: ['check-history', check.id],
    queryFn: ({ signal }) => monitoring.history(check.id, 24, signal),
    refetchInterval: 120_000,
  });

  const runNow = useMutation({
    mutationFn: () => monitoring.runNow(check.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['checks'] });
      void queryClient.invalidateQueries({ queryKey: ['check-history', check.id] });
      setRanAt(new Date().toISOString());
    },
  });

  // Focus-trap-lite: Esc closes, focus lands in the drawer.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    scrimRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isHourly = check.id.endsWith('solve-practice.hourly');
  const rows = buildRows(check.value);
  const columns = rows.length > 0 ? Object.keys(rows[0]!) : [];
  // Expandable rows: a row with a long text cell (the SQL of long-queries)
  // collapses the cell to a preview; clicking the row reveals the full text.
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const longCellKeys = (row: RowShape): string[] =>
    columns.filter((key) => typeof row[key] === 'string' && (row[key] as string).length > 100);

  return (
    <div className="drawer-scrim" ref={scrimRef} tabIndex={-1} onClick={(event) => event.target === event.currentTarget && onClose()}>
      <div className="drawer" role="dialog" aria-modal="true" aria-label={check.title}>
        <div className="check-tile__head">
          <h3>{check.title}</h3>
          <StatusDot status={check.status} />
        </div>
        {check.description ? <p className="drawer__description">{check.description}</p> : null}
        <p className="drawer__why">{check.why}</p>
        <div className="drawer__actions">
          <Button variant="secondary" size="sm" loading={runNow.isPending} onClick={() => runNow.mutate()}>
            Запустить сейчас
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Закрыть
          </Button>
          {runNow.isError ? <span className="form-error">не удалось запустить</span> : null}
          <span className="check-tile__meta">
            обновлено: {ranAt ? new Date(ranAt).toLocaleTimeString('ru-RU') : '—'} · {check.durationMs ?? '—'} мс
          </span>
        </div>
        {check.error ? <p className="form-error">{check.error}</p> : null}

        <AlertSettings check={check} />

        {isHourly ? <HourlyChart check={check} /> : <Sparkline points={history.data?.items ?? []} />}

        {rows.length > 0 && columns.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                {columns.map((key) => (
                  <th key={key}>{key}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 50).map((row, index) => {
                const longKeys = longCellKeys(row);
                const expandable = longKeys.length > 0;
                const isExpanded = expandedRow === index;
                const toggle = (): void => setExpandedRow(isExpanded ? null : index);
                return (
                  <Fragment key={index}>
                    <tr
                      className={expandable ? 'data-table__row--expandable' : undefined}
                      onClick={expandable ? toggle : undefined}
                      onKeyDown={
                        expandable
                          ? (event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                toggle();
                              }
                            }
                          : undefined
                      }
                      tabIndex={expandable ? 0 : undefined}
                      aria-expanded={expandable ? isExpanded : undefined}
                    >
                      {columns.map((key, colIndex) => {
                        const raw = row[key];
                        const truncated = expandable && typeof raw === 'string' && raw.length > 100;
                        return (
                          <td key={key}>
                            {truncated && colIndex === 0 ? (
                              <span className="data-table__chevron" aria-hidden="true">
                                {isExpanded ? '▾ ' : '▸ '}
                              </span>
                            ) : null}
                            {truncated ? `${(raw as string).slice(0, 100)}…` : formatCell(raw)}
                          </td>
                        );
                      })}
                    </tr>
                    {isExpanded ? (
                      <tr className="data-table__expanded-row">
                        <td colSpan={columns.length}>
                          {longKeys.map((key) => (
                            <pre key={key} className="data-table__expanded-pre">
                              {String(row[key])}
                            </pre>
                          ))}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="drawer__why">{typeof check.value === 'object' ? 'нет табличных данных' : String(check.value)}</p>
        )}
      </div>
    </div>
  );
}
